import { computed, ref } from 'vue';

/**
 * Composable to manage a delayed function execution with a countdown.
 *
 * @param {object} options
 * @param {number} options.delayMs - The total delay in milliseconds.
 * @param {Function} options.onBeforeStart - Callback function to execute before the delay starts.
 * @param {Function} options.onComplete - Callback function to execute when the delay completes.
 * @param {Function} options.onCancel - Callback function to execute when the delay is cancelled.
 * @param {number} [options.updateIntervalMs] - The interval in milliseconds at which the countdown state updates.
 */
export function useDelayFn({
  delayMs,
  onBeforeStart,
  onComplete,
  onCancel,
  updateIntervalMs = 50,
}) {
  const countdown = ref(0); // Remaining time in seconds
  const isExecuting = ref(false); // Or isDelaying, isPending

  let timeoutId = null;
  let intervalId = null;

  const countdownPercentage = computed(() => {
    if (!isExecuting.value || delayMs <= 0) {
      return 0;
    }
    const initialTotalSeconds = delayMs / 1000;
    const percentage =
      ((initialTotalSeconds - countdown.value) / initialTotalSeconds) * 100;
    return Math.max(0, Math.min(100, percentage));
  });

  function start(...args) {
    onBeforeStart?.(...args);
    if (isExecuting.value) {
      // Optionally, decide if starting again should reset or be ignored
      console.warn('Delay function already in progress.');
      return;
    }

    isExecuting.value = true;
    countdown.value = delayMs / 1000;

    if (intervalId) {
      clearInterval(intervalId);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const decrementAmount = updateIntervalMs / 1000;

    intervalId = setInterval(() => {
      countdown.value = Math.max(0, countdown.value - decrementAmount);
      if (countdown.value === 0) {
        clearInterval(intervalId);
      }
    }, updateIntervalMs);

    timeoutId = setTimeout(() => {
      onComplete(...args);
      isExecuting.value = false;
      countdown.value = 0;
      timeoutId = null;
      intervalId = null; // Ensure interval ID is also cleared here
    }, delayMs);
  }

  function cancel(...args) {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    onCancel?.(...args);
    isExecuting.value = false;
    countdown.value = 0;
  }

  return {
    isExecuting,
    countdown,
    countdownPercentage,
    start,
    cancel,
  };
}
